import { combineReducers } from 'redux';
import { takeLatest } from 'redux-saga/effects';
import { simpleAsyncSaga, throttledSaga } from '../helpers/EzeeSaga';
import { EzeeAsyncAction } from '../helpers/EzeeAsyncAction';

import { User, ListResponse } from '../api/apiTypes';
import { MainReducerState, RequestState } from '../reducers';

import {
    ListPayload,
    IdPayload,
    DataPayload,
    list as listApiCall,
    details as detailsApiCall,
    create as createApiCall,
    update as updateApiCall,
    del as deleteApiCall,
} from '../api/users';

// State

export interface UsersState {
    list: RequestState<ListResponse<User>>;
    details: RequestState<User>;
    create: RequestState<User>;
    update: RequestState<Partial<User>>;
    delete: RequestState<User>;
}

const initialState: UsersState = {
    details: {
        data: undefined,
        loading: false,
        success: false,
    },
    list: {
        data: {
            items: [],
            totalCount: 0,
            page: 0,
            pageSize: 20,
            pageCount: 0,
        },
        loading: false,
        success: false,
    },
    create: {
        loading: false,
        success: false,
    },
    update: {
        loading: false,
        success: false,
    },
    delete: {
        loading: false,
        success: false,
    },
};

// Actions/Reducers

export const list = new EzeeAsyncAction<
    UsersState['list'],
    ListPayload,
    ListResponse<User>
>('users/list', initialState.list, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
        error: undefined,
        success: undefined,
    }),
    success: (state, payload) => ({
                data: payload,
                loading: false,
                success: true,
                error: false,
            }
    ),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        success: false,
        error: payload,
    }),
    reset: (state) => ({
        ...initialState.list,
    }),
});

export const details = new EzeeAsyncAction<
    UsersState['details'],
    IdPayload,
    User
>('users/details', initialState.details, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
        error: undefined,
        success: undefined,
    }),
    success: (state, payload) => ({
                data: payload,
                loading: false,
                success: true,
                error: false,
            }
    ),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        success: false,
        error: payload,
    }),
    reset: (state) => ({
        ...initialState.details,
    }),
});

export const create = new EzeeAsyncAction<
    UsersState['create'],
    DataPayload,
    User
>('users/create', initialState.create, {
    trigger: (state, payload) => ({
        ...state,
        ...payload,
        loading: true,
        success: undefined,
        error: undefined,
    }),
    success: (state, payload) => ({
        data: payload,
        loading: false,
        success: true,
        error: undefined,
    }),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        success: false,
        error: payload,
    }),
    reset: (state) => ({
        ...initialState.create,
    }),
});

export const update = new EzeeAsyncAction<
    UsersState['update'],
    DataPayload,
    Partial<User>
>('users/update', initialState.update, {
    trigger: (state, payload) => ({
        ...state,
        ...payload,
        loading: true,
        success: undefined,
        error: undefined,
    }),
    success: (state, payload) => ({
        ...state,
        data: payload,
        loading: false,
        success: true,
        error: undefined,
    }),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        success: false,
        error: payload,
    }),
    reset: (state) => ({
        ...initialState.update,
    }),
});

export const del = new EzeeAsyncAction<
    UsersState['delete'],
    IdPayload,
    User
>('users/delete', initialState.delete, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
        error: undefined,
        success: undefined,
    }),
    success: (state, payload) => ({
        data: payload,
        loading: false,
        success: true,
        error: false,
    }),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        success: false,
        error: payload,
    }),
    reset: (state) => ({
        ...initialState.delete,
    }),
});

// Reducer

export const usersReducer = combineReducers<UsersState>({
    list: list.reducer,
    details: details.reducer,
    create: create.reducer,
    update: update.reducer,
    delete: del.reducer,
});

// Saga

export function* usersSaga() {
    yield takeLatest(list.type.trigger, throttledSaga(listApiCall, list));
    yield takeLatest(details.type.trigger, simpleAsyncSaga(detailsApiCall, details));
    yield takeLatest(create.type.trigger, simpleAsyncSaga(createApiCall, create));
    yield takeLatest(update.type.trigger, simpleAsyncSaga(updateApiCall, update));
    yield takeLatest(del.type.trigger, simpleAsyncSaga(deleteApiCall, del));
}

// Store helpers

export const getUsersListState = (state: MainReducerState) => state.users.list;
export const getUserDetailsState = (state: MainReducerState) => state.users.details;
export const getUserCreateState = (state: MainReducerState) => state.users.create;
export const getUserUpdateState = (state: MainReducerState) => state.users.update;
export const getUserDeleteState = (state: MainReducerState) => state.users.delete;
